import { combineSignals } from "../combineSignals";
import { useContext, useMemo } from "react";
import { ConfigContext, AuthContext } from "@@components";
import ApiConfig, { IApiConfig } from "../../api/ApiConfig";
import { createScopedLogger } from "../logger";
import { notifyDesignerAuthenticationRequired } from "../../UserInterfaceModule/Utils/designerAuthentication";
import { IsDesignMode } from "../../UserInterfaceModule/Utils/runtimeDetection";

import { designerRequestOptions } from "../../UserInterfaceModule/Utils/designerCachePolicy";

const logger = createScopedLogger("useApiClient");

// Helper class to create the custom http object
class HttpProvider {
  private config: IApiConfig;
  private isRedirecting = false; // Prevent multiple simultaneous redirects

  constructor(config: IApiConfig, private readonly designerOrigin?: string) {
    this.config = config;
  }

  fetch = async (url: RequestInfo, init?: RequestInit): Promise<Response> => {
    // Ensure cookies are sent with the request
    const requestInit: RequestInit = {
      ...init,
      credentials: "include",
    };

    const response = await fetch(url, designerRequestOptions(requestInit));

    // Check if the response is 401 Unauthorized
    if (response.status === 401 && IsDesignMode) {
      notifyDesignerAuthenticationRequired(this.designerOrigin, "unauthorized");
      return response;
    }

    if (response.status === 401 && this.config.shouldRedirectOnUnauthorized() && !this.isRedirecting) {
      this.isRedirecting = true;

      const urlString = typeof url === "string" ? url : url.toString();
      logger.warn(
        `API call to ${urlString} returned 401 Unauthorized. Token may be expired. Redirecting to sign in.`,
      );

      // Small delay to prevent overwhelming the auth system
      setTimeout(async () => {
        try {
          // Preserve current path when triggering sign-in
          const currentPath = window.location.pathname + window.location.search;
          await this.config.getAuthService().signIn(currentPath);
        } catch (error) {
          logger.error("Error during sign-in redirect:", error);
        } finally {
          this.isRedirecting = false;
        }
      }, 100);

      // Return the original response so the calling code can handle it appropriately
      return response;
    }

    return response;
  };
}

function useApiClient<T>(
  Factory: new (
    config: IApiConfig,
    baseUrl: string,
    http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> },
  ) => T,
  options?: { redirectOnUnauthorized?: boolean; signal?: AbortSignal },
): T {
  const { API_URL, DESIGNER_ORIGIN, EMBED_FETCH } = useContext(ConfigContext);
  const auth = useContext(AuthContext);
  const redirectOnUnauthorized = options?.redirectOnUnauthorized ?? auth.isAuthenticated;
  const lifetimeSignal = options?.signal;
  const client = useMemo(() => {
    const config = new ApiConfig(auth.authService, redirectOnUnauthorized);
    const httpProvider = new HttpProvider(config, DESIGNER_ORIGIN);
    // Pass the custom http object to the specific client
    const transport = EMBED_FETCH ? { fetch: EMBED_FETCH } : httpProvider;
    const scopedTransport = lifetimeSignal ? {
      fetch: async (url: RequestInfo, init?: RequestInit) => {
        const combined = combineSignals([lifetimeSignal, ...(init?.signal ? [init.signal] : [])]);
        try { return await transport.fetch(url, { ...init, signal: combined.signal }); }
        finally { combined.dispose(); }
      }
    } : transport;
    return new Factory(config, API_URL, scopedTransport);
  }, [Factory, auth.authService, API_URL, DESIGNER_ORIGIN, redirectOnUnauthorized, EMBED_FETCH, lifetimeSignal]);
  return client as T;
}

export default useApiClient;
